<template>
    <div
        class="slice-content-columns-duo-column"
        :class="`slice-content-columns-duo-column--align-${align}`"
        :data-mui-theme="theme"
        :data-testid="`slice-content-columns-duo-column--${index}`"
    >
        <MuiTag v-if="isFilled.keyText(tag)" revert :data-testid="`slice-content-columns-duo-tag--${index}`">
            {{ tag }}
        </MuiTag>

        <PrismicRichText
            v-if="isFilled.richText(title)"
            :field="title"
            class="slice-content-columns-duo-column__title"
            :data-testid="`slice-content-columns-duo-title--${index}`"
        />

        <PrismicRichText
            v-if="isFilled.richText(description)"
            :field="description"
            class="slice-content-columns-duo-column__description"
            :data-testid="`slice-content-columns-duo-description--${index}`"
        />

        <div
            v-if="visibleImages.length || visibleHighlights.length"
            class="slice-content-columns-duo-column__stage"
            :data-testid="`slice-content-columns-duo-stage--${index}`"
        >
            <div v-if="visibleImages.length" class="slice-content-columns-duo-column__media">
                <MktMaltPrismicImage
                    v-for="(item, imageIndex) in visibleImages"
                    :key="imageIndex"
                    :image-field="item.image"
                    :alt="item.image.alt ?? undefined"
                    loading="lazy"
                    class="slice-content-columns-duo-column__image"
                    :class="`slice-content-columns-duo-column__image--slot-${imageIndex + 1}`"
                    :data-testid="`slice-content-columns-duo-image--${index}-${imageIndex}`"
                />
            </div>
            <div v-if="visibleHighlights.length" class="slice-content-columns-duo-column__highlights">
                <MuiHighlight v-for="(item, highlightIndex) in visibleHighlights" :key="highlightIndex">
                    <PrismicRichText
                        :field="item"
                        class="slice-content-columns-duo-column__highlight"
                        :data-testid="`slice-content-columns-duo-highlight--${index}-${highlightIndex}`"
                    />
                </MuiHighlight>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import type {ImageField, KeyTextField, RichTextField} from '@prismicio/types';
    import {PrismicRichText} from '@prismicio/vue';
    import {MktMaltPrismicImage} from '#components';
    import {MuiTag, MuiHighlight} from '#components';

    type DuoColumnTheme = 'default' | 'primary' | 'secondary';

    type DuoImageItem = {
        image: ImageField;
    };

    interface Props {
        tag: KeyTextField;
        title: RichTextField;
        description: RichTextField;
        theme: DuoColumnTheme;
        images: DuoImageItem[];
        highlights: RichTextField[];
        index: number;
        align?: 'start' | 'end';
    }

    const props = withDefaults(defineProps<Props>(), {
        align: 'start',
    });

    const visibleImages = computed((): DuoImageItem[] => props.images.filter((item) => isFilled.image(item.image)).slice(0, 3));
    const visibleHighlights = computed((): RichTextField[] => props.highlights.filter((item) => isFilled.richText(item)).slice(0, 2));
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-content-columns-duo-column {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: var(--joy-core-spacing-4);
        width: 100%;

        @include mq.screen_md(min) {
            max-width: 500px;
        }

        // Content
        &__title {
            margin: 0;
            color: var(--joy-color-neutral-60);

            @include text.getFontBySize(primary-650);

            h2,
            h3 {
                margin: 0;
                font: inherit;
                color: inherit;
            }

            @include mq.screen_md(min) {
                @include text.getFontBySize(primary-850);
            }
        }

        &__description {
            margin: 0;
            color: var(--joy-color-neutral-60);

            @include text.getFontBySize(primary-400);
        }

        // Image stage: absolutely placed cards inside a ratio box, so slot percentages scale with the column
        &__stage {
            width: 100%;
            margin-top: var(--joy-core-spacing-4);
        }

        &__media {
            position: relative;
            width: 100%;
            max-width: 550px;
            margin-inline: auto;
            aspect-ratio: 5 / 4;

            // Soft halo tinted by the column theme, bleeding outside the stage
            &::before {
                content: '';
                position: absolute;
                inset: -12% 0;
                z-index: 0;
                background: radial-gradient(closest-side, var(--mui-color-background) 0%, transparent 100%);
                pointer-events: none;

                @include mq.screen_md(min) {
                    inset: -15% -12%;
                }
            }
        }

        &__image {
            position: absolute;
            z-index: 1;
            height: auto;
            border-radius: var(--joy-core-radius-3);
        }

        &__highlights {
            display: flex;
            flex-flow: row wrap;
            justify-content: center;
            gap: var(--joy-core-spacing-4);
            margin-top: var(--joy-core-spacing-4);

            @include mq.screen_md(min) {
                margin-top: var(--joy-core-spacing-8);
            }

            .mui-highlight.joy-highlight.joy-highlight__accent {
                display: flex;
                flex: 1 1 calc(50% - var(--joy-core-spacing-4) / 2);
                min-width: 220px;
                margin: 0;
            }

            .mui-highlight.joy-highlight > * {
                text-wrap: balance;

                @include text.getFontBySize(primary-300);
            }
        }

        // Collage slots, in CMS order
        &--align-start {
            .slice-content-columns-duo-column__image--slot-1 {
                top: 15%;
                left: 0;
                width: 52%;
            }

            .slice-content-columns-duo-column__image--slot-2 {
                bottom: -8%;
                left: 40%;
                z-index: 3;
                width: 62%;
            }

            .slice-content-columns-duo-column__image--slot-3 {
                top: 10%;
                left: 52%;
                z-index: 2;
                width: 50%;
            }

            .slice-content-columns-duo-column__highlights {
                padding-top: var(--joy-core-spacing-4);
            }
        }

        &--align-end {
            .slice-content-columns-duo-column__media {
                aspect-ratio: 5 / 3;
            }

            .slice-content-columns-duo-column__image--slot-1 {
                top: 25%;
                left: -3%;
                z-index: 2;
                width: 56%;
            }

            .slice-content-columns-duo-column__image--slot-2 {
                top: 70%;
                left: 2%;
                z-index: 2;
                width: 44%;
            }

            .slice-content-columns-duo-column__image--slot-3 {
                top: 8%;
                right: 0;
                width: 54%;
            }

            @include mq.screen_md(min) {
                align-items: flex-end;
                text-align: right;
            }
        }
    }
</style>
